import React from 'react';
import { getUnattestedScript } from '../../../../data/treatment/getUnattestedScript';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { FormInput } from '../../../../components/forms/FormInput';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import useChart from '../../../../hooks/useChart';
import { Chart } from '../../../../types/Chart/Chart';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { PatientDoc } from '../../../../types/Patient/PatientDoc';
import { Button } from '../../../../components/buttons/Button';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { Uploader } from '../../../../components/Uploader';

import { markPreviousPageCompleted } from '../../../../data/navigation/utilities/markPreviousPageCompleted';
import { getNextPage } from '../../../../data/navigation/getNextPage';
import { NavForward } from '../../../../components/buttons/NavForward';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

import { FieldInfo } from '../../../../components/FieldInfo';

import * as Sentry from '@sentry/browser';
import Checkbox from '../../../../components/forms/Checkbox';

import { PhotoCamera } from '../../../../components/PhotoCamera';
import { FileTypes } from '../../../../types/Files/FileTypes';
import { PhotoDimensions } from '../../../../types/Camera/PhotoDimensions';
import { FormSelect } from '../../../../components/forms/FormSelect';

export const Route = createFileRoute('/_authenticated/$patientId/info/hcaInfo')({
  component: HCAInfoPage,
});

function HCAInfoPage() {
  const { mutate } = useUpdateChartSection();
  const { patientId } = Route.useParams<{ patientId: string }>();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const [nextPageURL, setNextPageURL] = React.useState<string | undefined>(undefined);
  const [prevMarked, setPrevMarked] = React.useState<boolean>(false);
  const [hcaInfoSameAsPatient, setHcaInfoSameAsPatient] = React.useState<boolean>(false);
  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const [hcaIsReader, setHcaIsReader] = React.useState<boolean>(false);
  const [photoOneWorking, setPhotoOneWorking] = React.useState(false);
  const [takingPhoto, setTakingPhoto] = React.useState({
    readerIdentificationFront: false,
  });
  const [HCANomination, setHCANomination] = React.useState<string>('unset');

  // INITIALIZE SCRIPT_REVIEW DOCUMENT IF NONE EXISTS
  React.useEffect(() => {
    if (chart && !chart.SCRIPT_REVIEW) {
      mutate(
        {
          patientId: patientId,
          docData: {
            SCRIPT_REVIEW: {
              attestedCode: '',
              scriptAttested: false,
              speaker: 'patient',
              telePrompterScript: '',
            },
          },
        },
        {
          onSuccess: async () => {
            // alert("Successfully updated document");
          },
          onError: (error) => {
            Sentry.captureException(error);
            alert(`Error updating document: ${error.message}`); // TELL THE USER
            console.error(error); // GET THE STACKTRACE
          },
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart, patientInfo]);

  // SET HAS READER FROM CHART
  React.useEffect(() => {
    if (chart?.SCRIPT_REVIEW?.speaker === 'reader') {
      setHcaIsReader(true);
    }
  }, [chart]);

  // REMOVE THE HCA AS READER ON CHECKBOX CLICK
  const handleRemoveReader = async () => {
    if (!chart) {
      // setAttested(false);
      return;
    }

    mutate(
      {
        patientId: patientId,
        docData: {
          SCRIPT_REVIEW: {
            attestedCode: chart.SELECTED_TREATMENT_OPTION?.code
              ? chart.SELECTED_TREATMENT_OPTION.code
              : chart.SCRIPT_REVIEW.attestedCode,
            scriptAttested: chart.SCRIPT_REVIEW.scriptAttested,
            speaker: 'patient',
            reader: {
              email: '',
              fullName: '',
              phoneNumber: '',
            },
            readerIdentificationFrontURI: '',
            telePrompterScript:
              chart.SELECTED_TREATMENT_OPTION?.unAttestedScript || chart.SCRIPT_REVIEW?.telePrompterScript,
          },
        },
      },
      {
        onSuccess: async () => {
          // alert("Successfully updated document");
          setHcaIsReader(!hcaIsReader);
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // ADD THE HCA AS READER ON CHECKBOX CLICK
  const handleAddReader = async () => {
    if (!chart) {
      // setAttested(false);
      return;
    }
    const code = chart.SELECTED_TREATMENT_OPTION?.code ? chart.SELECTED_TREATMENT_OPTION.code : 'fullCode';
    mutate(
      {
        patientId: patientId,
        docData: {
          SCRIPT_REVIEW: {
            attestedCode: chart.SELECTED_TREATMENT_OPTION?.code
              ? chart.SELECTED_TREATMENT_OPTION.code
              : chart.SCRIPT_REVIEW.attestedCode,
            scriptAttested: chart.SCRIPT_REVIEW.scriptAttested,
            speaker: 'hca',
            reader: {
              email: hcaInfoForm.state.values.agentEmail,
              fullName: hcaInfoForm.state.values.agent,
              phoneNumber: hcaInfoForm.state.values.cellPhone,
            },
            readerIdentificationFrontURI: chart.SCRIPT_REVIEW?.readerIdentificationFrontURI
              ? chart.SCRIPT_REVIEW?.readerIdentificationFrontURI
              : '',
            telePrompterScript: getUnattestedScript({ code, chart, patientInfo }),
          },
        },
      },
      {
        onSuccess: async () => {
          // alert("Successfully updated document");
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );

    mutate(
      {
        patientId: patientId,
        docData: {
          SELECTED_TREATMENT_OPTION: {
            code: chart.SELECTED_TREATMENT_OPTION?.code ? chart.SELECTED_TREATMENT_OPTION.code : 'fullCode',
            description: chart.SELECTED_TREATMENT_OPTION?.description
              ? chart.SELECTED_TREATMENT_OPTION.description
              : 'Full Code - All Treatment & No Limitations in Care',
            unAttestedScript: getUnattestedScript({ code, chart, patientInfo }),
          },
        },
      },
      {
        onSuccess: async () => {
          // alert("Successfully updated document");
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // HANDLE HCA AS READER CHECKBOX CLICK
  const handleHcaIsReaderCheckboxClick = () => {
    if (hcaIsReader) {
      handleRemoveReader();
      setHcaIsReader(false);
      return;
    } else {
      handleAddReader();
      setHcaIsReader(true);
    }
  };

  // INITIALIZE FORM
  const hcaInfoForm = useForm({
    defaultValues: {
      agent: chart?.PATIENT_INFO_1?.firstName ? chart.HEALTHCARE_AGENT_INFO?.agent : '',
      street: chart?.HEALTHCARE_AGENT_INFO?.street ? chart.HEALTHCARE_AGENT_INFO?.street : '',
      agentCity: chart?.HEALTHCARE_AGENT_INFO?.city ? chart.HEALTHCARE_AGENT_INFO?.city : '',
      agentState: chart?.HEALTHCARE_AGENT_INFO?.state ? chart.HEALTHCARE_AGENT_INFO?.state : '',
      agentZIP: chart?.HEALTHCARE_AGENT_INFO?.ZIP ? chart.HEALTHCARE_AGENT_INFO?.ZIP : '',
      agentCountry: chart?.HEALTHCARE_AGENT_INFO?.country ? chart.HEALTHCARE_AGENT_INFO?.country : '',
      agentEmail: chart?.HEALTHCARE_AGENT_INFO?.email ? chart.HEALTHCARE_AGENT_INFO?.email : '',
      cellPhone: chart?.HEALTHCARE_AGENT_INFO?.cellPhone ? chart.HEALTHCARE_AGENT_INFO?.cellPhone : '',
      agentHomePhone: chart?.HEALTHCARE_AGENT_INFO?.homePhone ? chart.HEALTHCARE_AGENT_INFO?.homePhone : '',
      agentToPatient: chart?.HEALTHCARE_AGENT_INFO?.agentToPatient ? chart.HEALTHCARE_AGENT_INFO?.agentToPatient : '',
      patientToAgent: chart?.HEALTHCARE_AGENT_INFO?.patientToAgent ? chart.HEALTHCARE_AGENT_INFO?.patientToAgent : '',
      hasPowerOfAttorney: chart?.HEALTHCARE_AGENT_INFO?.hasPowerOfAttorney
        ? chart.HEALTHCARE_AGENT_INFO.hasPowerOfAttorney
        : 'unset',
    },
    onSubmit: () => {
      handleHCAInfoUpdate();
    },
  });

  // MARK PREVIOUS PAGE AS COMPLETED
  React.useEffect(() => {
    if (patientInfo && patientInfo.navObj && !prevMarked) {
      markPreviousPageCompleted({
        currentPageName: 'hcaInfo',
        patientInfo,
        value: true,
      });
      setPrevMarked(true);
    }
  }, [patientInfo, prevMarked]);

  // GET NEXT PAGE URL
  React.useEffect(() => {
    if (chart && patientInfo?.navObj) {
      (async () => {
        const awaitedUrl = await getNextPage({
          currentPage: 'hcaInfo',
          chart: chart,
          patientInfo: patientInfo,
        });
        setNextPageURL(awaitedUrl);
      })();
    }
  }, [chart, patientInfo]);

  // WRITE FORM DATA TO FIRESTORE
  const handleHCAInfoUpdate = () => {
    mutate(
      {
        patientId: patientId,
        docData: {
          HEALTHCARE_AGENT_INFO: {
            agent: hcaInfoForm.state.values.agent,
            street: hcaInfoForm.state.values.street,
            city: hcaInfoForm.state.values.agentCity,
            state: hcaInfoForm.state.values.agentState,
            ZIP: hcaInfoForm.state.values.agentZIP,
            country: hcaInfoForm.state.values.agentCountry,
            email: hcaInfoForm.state.values.agentEmail,
            cellPhone: hcaInfoForm.state.values.cellPhone,
            homePhone: hcaInfoForm.state.values.agentHomePhone,
            agentToPatient: hcaInfoForm.state.values.agentToPatient,
            patientToAgent: hcaInfoForm.state.values.patientToAgent,
            hasPowerOfAttorney: hcaInfoForm.state.values.hasPowerOfAttorney,
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
          setSubmitted(true);
        },
        onError: (error) => {
          Sentry.captureException(error); // SEND ERROR TO SENTRY
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // WRITE PATIENT INFO ADDRESS VALUES TO HCA INFO FORM
  const handleSameAsPatientCheckboxClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHcaInfoSameAsPatient(!hcaInfoSameAsPatient);
    if (e.target.checked) {
      hcaInfoForm.setFieldValue(
        'street',
        chart?.PATIENT_INFO_2?.streetAddress ? chart?.PATIENT_INFO_2?.streetAddress : '',
      );
      hcaInfoForm.setFieldValue('agentCity', chart?.PATIENT_INFO_2?.city ? chart?.PATIENT_INFO_2?.city : '');
      hcaInfoForm.setFieldValue('agentState', chart?.PATIENT_INFO_2?.state ? chart?.PATIENT_INFO_2?.state : '');
      hcaInfoForm.setFieldValue('agentZIP', chart?.PATIENT_INFO_2?.ZIP ? chart?.PATIENT_INFO_2?.ZIP : '');
      hcaInfoForm.setFieldValue('agentCountry', chart?.PATIENT_INFO_2?.country ? chart?.PATIENT_INFO_2?.country : '');
      hcaInfoForm.setFieldValue('cellPhone', chart?.PATIENT_INFO_2?.homePhone ? chart?.PATIENT_INFO_2?.homePhone : '');
    } else {
      setHcaInfoSameAsPatient(!hcaInfoSameAsPatient);
      hcaInfoForm.setFieldValue('street', '');
      hcaInfoForm.setFieldValue('agentCity', '');
      hcaInfoForm.setFieldValue('agentState', '');
      hcaInfoForm.setFieldValue('agentZIP', '');
      hcaInfoForm.setFieldValue('agentCountry', '');
      hcaInfoForm.setFieldValue('cellPhone', '');
    }
  };

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId) {
    return (
      <>
        <div id="hca-info-page" className="flex flex-col">
          <h1 className="place-self-center text-3xl font-bold">HEALTH CARE AGENT INFORMATION</h1>
          <div className="my-4 flex flex-col items-center">
            <div id="hca-info-container" className="flex flex-col items-center">
              <button
                className="ml-2 text-xl text-blue-500 underline"
                onClick={() => {
                  alert(
                    'A healthcare agent is someone who can speak for you during a medical emergency when you cannot speak for yourself.\r\rIf you have not yet named a healthcare agent, you can name one here today, but you should be sure to communicate this to your newly named agent.\r\rIf you are unwilling/unable to name an agent today, you will need to obtain a MIDEO through our guided process. Please call 1-844-MIDEO-4U to speak to a concierge.',
                  );
                }}
              >
                More Info
              </button>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  hcaInfoForm.handleSubmit();
                  window.scrollTo(0, document.body.scrollHeight);
                }}
                className="flex flex-col items-center"
              >
                <div id="same-as-patient" className="flex flex-row items-end">
                  <div className="mb-1 ml-2 flex flex-row items-center rounded-xl border-4 border-solid border-red-500 p-8 text-2xl font-bold">
                    <input
                      type="checkbox"
                      id="setAgentInfo"
                      name="setAgentInfo"
                      checked={hcaInfoSameAsPatient}
                      onChange={(e) => handleSameAsPatientCheckboxClick(e)}
                      className="h-8 w-8"
                    />
                    <label className="ml-2" htmlFor="setAgentInfo">
                      Health Care Agent Address Same as Patient
                    </label>
                  </div>
                </div>
                {/* <div id="nav" className="my-4 flex flex-col items-center">
                  {submitted && nextPageURL && <NavForward to={nextPageURL} disabled={false} />}
                </div> */}
                <div id="hca-info-form-inputs" className="ml-12 flex flex-col">
                  <hcaInfoForm.Field
                    name="agent"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return "Agent's name cannot be empty.";
                        } else if (value.length < 5) {
                          return "Agent's name should contain at least five characters.";
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Agent's Full Name*"
                          id="agent-name"
                          name={field.name}
                          value={field.state.value ? field.state.value : ''}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <>
                    <hcaInfoForm.Field
                      name="street"
                      validators={{
                        onChange({ value }) {
                          if (!value || value.length === 0) {
                            return 'Street address cannot be empty.';
                          } else if (value.length < 5) {
                            return 'Street address should contain at least five characters.';
                          } else {
                            return undefined;
                          }
                        },
                      }}
                      children={(field) => (
                        <>
                          <FormInput
                            label="Agent's Street Address*"
                            id="agent-street-address"
                            name={field.name}
                            value={field.state.value ? field.state.value : ''}
                            handleBlur={field.handleBlur}
                            handleChange={(e) => field.handleChange(e.target.value)}
                            required={true}
                          />
                          <FieldInfo fieldMeta={field.state.meta} />
                        </>
                      )}
                    />
                    <hcaInfoForm.Field
                      name="agentCity"
                      validators={{
                        onChange({ value }) {
                          if (!value || value.length === 0) {
                            return 'City cannot be empty.';
                          } else {
                            return undefined;
                          }
                        },
                      }}
                      children={(field) => (
                        <>
                          <FormInput
                            label="Agent's City*"
                            id="agent-city"
                            name={field.name}
                            value={field.state.value ? field.state.value : ''}
                            handleBlur={field.handleBlur}
                            handleChange={(e) => field.handleChange(e.target.value)}
                            required={true}
                          />
                          <FieldInfo fieldMeta={field.state.meta} />
                        </>
                      )}
                    />
                    <hcaInfoForm.Field
                      name="agentState"
                      validators={{
                        onChange({ value }) {
                          if (!value || value.length === 0) {
                            return 'State cannot be empty.';
                          } else {
                            return undefined;
                          }
                        },
                      }}
                      children={(field) => (
                        <>
                          <FormInput
                            label="Agent's State*"
                            id="agent-state"
                            name={field.name}
                            value={field.state.value ? field.state.value : ''}
                            handleBlur={field.handleBlur}
                            handleChange={(e) => field.handleChange(e.target.value)}
                            required={true}
                          />
                          <FieldInfo fieldMeta={field.state.meta} />
                        </>
                      )}
                    />
                    <hcaInfoForm.Field
                      name="agentZIP"
                      validators={{
                        onChange({ value }) {
                          if (!value || value.length === 0) {
                            return 'ZIP Code cannot be empty.';
                          } else {
                            return undefined;
                          }
                        },
                      }}
                      children={(field) => (
                        <>
                          <FormInput
                            label="Agent's ZIP Code*"
                            id="agent-ZIP"
                            name={field.name}
                            value={field.state.value ? field.state.value : ''}
                            handleBlur={field.handleBlur}
                            handleChange={(e) => field.handleChange(e.target.value)}
                            required={true}
                          />
                          <FieldInfo fieldMeta={field.state.meta} />
                        </>
                      )}
                    />
                    <hcaInfoForm.Field
                      name="agentCountry"
                      validators={{
                        onChange({ value }) {
                          if (!value || value.length === 0) {
                            return 'Country cannot be empty.';
                          } else {
                            return undefined;
                          }
                        },
                      }}
                      children={(field) => (
                        <>
                          <FormInput
                            label="Agent's Country*"
                            id="agent-country"
                            name={field.name}
                            value={field.state.value ? field.state.value : ''}
                            handleBlur={field.handleBlur}
                            handleChange={(e) => field.handleChange(e.target.value)}
                            required={true}
                          />
                          <FieldInfo fieldMeta={field.state.meta} />
                        </>
                      )}
                    />
                    <h2 className="text-xl font-bold">Agent&apos;s Cell Phone*</h2>
                    <hcaInfoForm.Field
                      name="cellPhone"
                      validators={{
                        onChange: ({ value }) =>
                          value.length <= 10 ? 'Cell Phone must contain at least 10 digits' : undefined,
                      }}
                      children={(field) => (
                        <>
                          <PhoneInput
                            country={'us'}
                            inputStyle={{ backgroundColor: '#374151', color: 'white', fontWeight: 'bold' }}
                            inputProps={{
                              name: 'cellPhone',
                              required: true,
                              autoFocus: true,
                            }}
                            placeholder="Enter phone number"
                            value={field.state.value ? field.state.value : ''}
                            onChange={(value) => {
                              if (value) {
                                field.setValue(value);
                              }
                            }}
                          />
                          <FieldInfo fieldMeta={field.state.meta} />
                        </>
                      )}
                    />
                    <h2 className="text-xl font-bold">Agent&apos;s Home Phone (Optional)</h2>
                    <hcaInfoForm.Field
                      name="agentHomePhone"
                      children={(field) => (
                        <>
                          <PhoneInput
                            country={'us'}
                            inputStyle={{ backgroundColor: '#374151', color: 'white', fontWeight: 'bold' }}
                            inputProps={{
                              name: 'agentHomePhone',
                              required: true,
                              autoFocus: true,
                            }}
                            placeholder="Enter phone number"
                            value={field.state.value ? field.state.value : ''}
                            onChange={(value) => {
                              if (value) {
                                field.setValue(value);
                              }
                            }}
                          />
                          <FieldInfo fieldMeta={field.state.meta} />
                        </>
                      )}
                    />
                    <hcaInfoForm.Field
                      name="agentEmail"
                      validators={{
                        onChange({ value }) {
                          if (!value || value.length === 0) {
                            return 'Email cannot be empty.';
                          } else {
                            return undefined;
                          }
                        },
                      }}
                      children={(field) => (
                        <>
                          <FormInput
                            label="Agent's Email Address*"
                            id="agentEmail"
                            name={field.name}
                            value={field.state.value ? field.state.value : ''}
                            handleBlur={field.handleBlur}
                            handleChange={(e) => field.handleChange(e.target.value)}
                            required={true}
                          />
                          <FieldInfo fieldMeta={field.state.meta} />
                        </>
                      )}
                    />
                    <hcaInfoForm.Field
                      name="agentToPatient"
                      validators={{
                        onChange({ value }) {
                          if (!value || value.length === 0) {
                            return "Agent's relationship to patient cannot be empty.";
                          } else {
                            return undefined;
                          }
                        },
                      }}
                      children={(field) => (
                        <>
                          <FormInput
                            label="Agent's Relationship To Patient*"
                            id="agent-to-patient"
                            placeholder='This person is my "___"'
                            name={field.name}
                            value={field.state.value ? field.state.value : ''}
                            handleBlur={field.handleBlur}
                            handleChange={(e) => field.handleChange(e.target.value)}
                            required={true}
                          />
                          <FieldInfo fieldMeta={field.state.meta} />
                        </>
                      )}
                    />
                    <hcaInfoForm.Field
                      name="patientToAgent"
                      validators={{
                        onChange({ value }) {
                          if (!value || value.length === 0) {
                            return "Patient's relationship to agent cannot be empty.";
                          } else {
                            return undefined;
                          }
                        },
                      }}
                      children={(field) => (
                        <>
                          <FormInput
                            label="Patient's Relationship To Agent*"
                            id="patient-to-agent"
                            placeholder='My agent calls me their "___"'
                            name={field.name}
                            value={field.state.value ? field.state.value : ''}
                            handleBlur={field.handleBlur}
                            handleChange={(e) => field.handleChange(e.target.value)}
                            required={true}
                          />
                          <FieldInfo fieldMeta={field.state.meta} />
                        </>
                      )}
                    />
                  </>
                </div>
                <div id="hca-nomination" className="ml-12 flex flex-col">
                  <hcaInfoForm.Field
                    name="hasPowerOfAttorney"
                    listeners={{
                      onMount: ({ value }) => {
                        setHCANomination(value);
                      },
                      onChange: ({ value }) => {
                        setHCANomination(value);
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormSelect
                          label="HCA Info*"
                          name="hca-info"
                          id="power-of-attorney-select"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                          options={[
                            { name: '--Please choose an option--', value: 'unset' },
                            {
                              name: 'I already appointed my HCA in a legal Healthcare Power of Attorney document',
                              value: 'yes',
                            },
                            { name: 'I am using the MIDEO process to designate this person as my HCA ', value: 'no' },
                          ]}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                </div>
                {/* {HCANomination !== 'unset' && ( */}
                <div id="more-hca-info" className="ml-12 flex flex-col">
                  {HCANomination === 'yes' && (
                    <div className="flex flex-col items-center">
                      <h2 className="text-xl font-bold">
                        Please provide a copy of the Healthcare Power of Attorney document
                      </h2>
                      <div>
                        {chart?.OTHER_DOCUMENTS?.powerOfAttorneyURI ? (
                          <>
                            <a
                              className="font-medium text-blue-600 hover:underline dark:text-blue-500"
                              href={chart?.OTHER_DOCUMENTS?.powerOfAttorneyURI}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <p className="text-xl">View current medical durable power of attorney</p>
                            </a>
                          </>
                        ) : (
                          <>
                            <Uploader
                              docToUpdate="OTHER_DOCUMENTS"
                              bucket="public"
                              type={FileTypes.powerOfAttorney}
                              title="Upload Medical Durable Power of Attorney"
                              elementId="powerOfAttorney-file-upload"
                            />
                          </>
                        )}
                      </div>
                    </div>
                  )}
                  {/* {HCANomination === 'no' && (
                    <div className="flex flex-col items-center">
                      <h2 className="text-xl font-bold">
                        Here is some more info on naming a Healthcare Agent through the MIDEO process
                      </h2>
                    </div>
                  )} */}
                </div>
                {/* )} */}

                <div id="hca-as-reader" className="mt-4 flex flex-col items-center">
                  <Checkbox
                    id="reader-checkbox"
                    name="reader"
                    onChange={() => {
                      handleHcaIsReaderCheckboxClick();
                    }}
                    checked={hcaIsReader}
                    label={`I am unable to read the script myself. My healthcare agent named above will read it for me today.`}
                  />
                  {hcaIsReader && (
                    <div className="flex flex-col items-center">
                      <div id="reader-id-container" className="mt-4 flex flex-col">
                        <p className="text-2xl font-bold">
                          Please provide a photo of the agent&apos;s government issued ID for video verification.
                        </p>
                        {chart?.SCRIPT_REVIEW?.readerIdentificationFrontURI &&
                        takingPhoto.readerIdentificationFront === false ? (
                          <div className="flex flex-row items-center">
                            <img
                              src={chart?.SCRIPT_REVIEW?.readerIdentificationFrontURI}
                              style={{ width: `${PhotoDimensions.desktopWidth}%` }}
                              alt="Reader ID Front"
                            />
                            <div className="ml-4">
                              <Button
                                id="retake-reader-id-front"
                                type="button"
                                textContent="Retake Photo"
                                onClick={() => {
                                  setTakingPhoto({ ...takingPhoto, readerIdentificationFront: true });
                                }}
                              />
                            </div>
                          </div>
                        ) : (
                          takingPhoto.readerIdentificationFront === false && (
                            <Button
                              id="take-reader-id-front"
                              type="button"
                              textContent="Take Photo"
                              onClick={() => {
                                setTakingPhoto({ ...takingPhoto, readerIdentificationFront: true });
                              }}
                            />
                          )
                        )}
                        {takingPhoto.readerIdentificationFront && (
                          <PhotoCamera
                            type={FileTypes.readerIdFront}
                            docToUpdate="SCRIPT_REVIEW"
                            style={'card'}
                            bucket={'patient'}
                            path={`user/${patientId}/images/readerIdentificationFront`}
                            patientId={patientId}
                            hideCamera={() => {
                              setPhotoOneWorking(false);
                              setTakingPhoto({ ...takingPhoto, readerIdentificationFront: false });
                            }}
                            working={photoOneWorking}
                            setWorking={setPhotoOneWorking}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div id="form-submit-button" className="mt-8 flex flex-col items-center">
                  <hcaInfoForm.Subscribe
                    selector={(state) => [state.canSubmit]}
                    children={([canSubmit]) => (
                      <>
                        {!canSubmit && (
                          <div className="mt-4 flex flex-col items-center">
                            <p className="text-red-500">Please correct any errors above before re-submitting.</p>
                          </div>
                        )}
                        <Button
                          id="submit-hca-info"
                          type="submit"
                          disabled={
                            !canSubmit ||
                            HCANomination === 'unset' ||
                            (hcaIsReader && !chart?.SCRIPT_REVIEW?.readerIdentificationFrontURI) ||
                            (HCANomination === 'yes' && !chart?.OTHER_DOCUMENTS?.powerOfAttorneyURI)
                          }
                          onClick={hcaInfoForm.handleSubmit}
                          textContent="Submit"
                          submitted={submitted}
                        />
                      </>
                    )}
                  />
                </div>
              </form>
            </div>
          </div>
          <div id="nav" className="my-4 flex flex-col items-center">
            {submitted && nextPageURL && <NavForward to={nextPageURL} disabled={false} />}
          </div>
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
